import React from 'react';
import { useNavigate } from 'react-router-dom';
import './TermsOfService.css';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="terms-of-service-page">
      <div className="terms-container">
        <button className="back-button" onClick={handleBack}>
          <span className="back-arrow">←</span>
          Back
        </button>
        
        <div className="terms-content">
          <h1>CaseKaizen Terms of Use</h1>
          <p className="last-updated">Last Updated: October 18, 2025</p>
          
          <div className="terms-text">
            <p>
              Welcome to CaseKaizen ("we", "our", or "us"). These Terms of Use ("Terms") govern your 
              access to and use of our website, applications, and services (collectively, the "Platform"). 
              By accessing or using CaseKaizen, you agree to be bound by these Terms. If you do not agree, 
              please discontinue use of the Platform immediately.
            </p>

            <h2>1. Eligibility</h2>
            <p>
              You must be at least 18 years old and capable of entering into a legally binding agreement 
              to use the Platform. By using the Platform, you represent that you meet these requirements 
              and that all information you provide is accurate and complete.
            </p>

            <h2>2. Account Registration</h2>
            <p>
              To access certain features, you may need to create an account. You are responsible for 
              maintaining the confidentiality of your credentials and for all activity under your account. 
              Notify CaseKaizen immediately at{' '}
              <a href="mailto:info@casekaizen.com">info@casekaizen.com</a> of any unauthorized use or 
              breach of security.
            </p>
            <p>
              We reserve the right to suspend or terminate your account if we believe your use violates 
              these Terms or applicable law.
            </p>

            <h2>3. Use of the Platform</h2>
            <p>You agree to use the Platform only for lawful purposes and in accordance with these Terms.</p>
            <p>You may not:</p>
            <ul>
              <li>Access or use the Platform to develop a competing product or service.</li>
              <li>Attempt to gain unauthorized access to systems or data.</li>
              <li>Reverse engineer, decompile, or copy the Platform's source code.</li>
              <li>Upload or transmit any malicious code, or interfere with the Platform's operation.</li>
            </ul>

            <h2>4. Service Availability and Modifications</h2>
            <p>
              We strive to ensure continuous service availability but do not guarantee uninterrupted access. 
              CaseKaizen may modify, suspend, or discontinue any part of the Platform at any time, with or 
              without notice.
            </p>

            <h2>5. Intellectual Property</h2>
            <p>
              All content, features, and functionality of the Platform—including software, text, graphics, 
              trademarks, and logos—are owned by CaseKaizen or its licensors. You are granted a limited, 
              non-exclusive, revocable license to use the Platform solely for its intended business purposes.
            </p>
            <p>
              You may not copy, modify, or distribute any part of the Platform without prior written consent.
            </p>

            <h2>6. Data and Privacy</h2>
            <p>
              By using the Platform, you consent to the collection and use of your information as described 
              in our <a href="/privacy-policy">Privacy Policy</a>.
            </p>
            <p>
              We comply with applicable data protection laws, including PIPEDA (Canada) and CCPA (U.S.), and 
              maintain reasonable safeguards to protect user data.
            </p>
            <p>
              You are responsible for ensuring that any personal or client data you upload complies with 
              your own privacy and regulatory obligations.
            </p>

            <h2>7. Subscription, Payments, and Termination</h2>
            <p>
              Access to certain Platform features may require a paid subscription. Fees, payment terms, and 
              renewal conditions will be detailed at the time of subscription.
            </p>
            <p>
              CaseKaizen may suspend or terminate your access if payment is not received, or if we believe 
              your use violates these Terms.
            </p>
            <p>
              You may cancel your account at any time by contacting{' '}
              <a href="mailto:info@casekaizen.com">info@casekaizen.com</a>. Upon termination, your access will 
              cease, and CaseKaizen may delete your data after a reasonable retention period in accordance 
              with our Privacy Policy.
            </p>

            <h2>8. Confidentiality</h2>
            <p>
              Both parties agree to maintain the confidentiality of non-public information obtained through 
              use of the Platform. This obligation survives termination of these Terms.
            </p>

            <h2>9. Disclaimers</h2>
            <p>
              The Platform is provided on an "as-is" and "as-available" basis. CaseKaizen makes no warranties, 
              express or implied, including merchantability, fitness for a particular purpose, or non-infringement.
            </p>
            <p>
              We do not guarantee the accuracy or completeness of data processed through the Platform.
            </p>

            <h2>10. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, CaseKaizen shall not be liable for any indirect, 
              incidental, or consequential damages arising out of or related to your use of the Platform, 
              including data loss or business interruption.
            </p>
            <p>
              Our total liability shall not exceed the amount paid by you to CaseKaizen in the 12 months 
              preceding the event giving rise to the claim.
            </p>

            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless CaseKaizen, its affiliates, and personnel from any 
              claims, losses, or expenses arising from your use of the Platform or violation of these Terms.
            </p>

            <h2>12. Governing Law</h2>
            <p>
              These Terms are governed by the laws of New Hampshire, United States, and Ontario, Canada, 
              without regard to conflicts of law principles. Any disputes shall be resolved exclusively in 
              the competent courts of those jurisdictions.
            </p>

            <h2>13. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. The "Last Updated" date reflects the most recent 
              version. Continued use of the Platform after changes means you accept the revised Terms.
            </p>

            <h2>14. Contact Us</h2>
            <p>If you have any questions about these Terms, please contact us at:</p>
            <div className="contact-info">
              <p><strong>CaseKaizen</strong></p>
              <p>📍 200 Innovative Way, Nashua, NH 03062</p>
              <p>📧 <a href="mailto:info@casekaizen.com">info@casekaizen.com</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
